import Cookies from 'js-cookie';
import { print } from 'graphql';
import settings from 'config/settings';
import isRecord from 'utils/isRecord';
import { TokenDocument } from 'graphql/generated/graphql';

export interface IRefreshedToken {
  accessToken: string;
  refreshToken: string | null;
}

const ACCESS_TOKEN_EXP_COOKIE_KEY = 'accessTokenExp';
const REFRESH_TIMEOUT_MS = 10_000;

/** The token of a `token` mutation response, or null when the response has no access token. */
const tokenOf = (body: unknown): IRefreshedToken | null => {
  if (!isRecord(body) || !isRecord(body.data) || !isRecord(body.data.token)) return null;
  const { accessToken, refreshToken } = body.data.token;
  if (typeof accessToken !== 'string' || !accessToken) return null;
  return { accessToken, refreshToken: typeof refreshToken === 'string' ? refreshToken : null };
};

/**
 * The session: the backend sets the access token as an httpOnly cookie and its expiry in the readable
 * `accessTokenExp` cookie; the refresh token lives in localStorage and is exchanged for a new access token (and a
 * rotated refresh token) with the `token` mutation, sent with fetch so it never waits for the Apollo link chain.
 */
class RefreshTokenServiceClass {
  private readonly storageKey = 'refreshToken';

  getRefreshToken = (): string | null => {
    try {
      return localStorage.getItem(this.storageKey);
    } catch {
      return null;
    }
  };

  setRefreshToken = (refreshToken: string | null | undefined): void => {
    try {
      localStorage.setItem(this.storageKey, refreshToken || '');
    } catch {
      // Storage can be unavailable (private mode, disabled storage); the session then lasts as long as the
      // access token.
    }
  };

  clearRefreshToken = (): void => {
    this.setRefreshToken('');
  };

  /** True without a refresh token or without the expiry cookie, otherwise while the access token has not expired. */
  isTokenValidOrUndefined = (): boolean => {
    const tokenExp = Cookies.get(ACCESS_TOKEN_EXP_COOKIE_KEY);
    const refreshToken = this.getRefreshToken();
    if (!tokenExp || !refreshToken) return true;
    const exp = parseInt(tokenExp, 10);
    return !(exp < Date.now());
  };

  /** Calls the `token` mutation with the stored refresh token and stores the rotated refresh token. */
  fetchNewAccessToken = async (): Promise<IRefreshedToken> => {
    const refreshToken = this.getRefreshToken();
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), REFRESH_TIMEOUT_MS);

    let body: unknown;
    try {
      const response = await fetch(String(settings.graphqlServerUrl), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
        body: JSON.stringify({
          operationName: 'Token',
          query: print(TokenDocument),
          variables: { refreshToken },
        }),
      });
      body = await response.json();
    } catch (e) {
      throw new Error('Failed to fetch fresh access token', { cause: e });
    } finally {
      window.clearTimeout(timeoutId);
    }

    const token = tokenOf(body);
    if (!token) throw new Error('No access token in the refresh response');
    if (token.refreshToken) this.setRefreshToken(token.refreshToken);
    return token;
  };
}

export default new RefreshTokenServiceClass();
